import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, Receipt, Download, Mail, MessageSquare, Send } from 'lucide-react';
import { Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import StyledInvoiceDisplay from './StyledInvoiceDisplay';
import InvoiceEmailModal from './InvoiceEmailModal';
import InvoiceSMSModal from './InvoiceSMSModal';
import { supabase } from '../lib/supabaseClient';
import { sendGmailEmail } from '../lib/email';
import { sendSMS } from '../lib/messaging';

interface ProductSaleSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleNumber: string;
  productSaleId?: string;
  saleData: {
    subtotal: number;
    taxAmount: number;
    total: number;
    discountAmount?: number;
    discountDetails?: {
      source: 'code' | 'custom';
      code?: string;
      description?: string;
      type: 'percentage' | 'fixed';
      value: number;
    };
    items: Array<{
      name: string;
      quantity: number;
      price: number;
    }>;
  };
  customer?: Customer | null;
  paymentMethod: string;
}

export default function ProductSaleSuccessModal({
  isOpen,
  onClose,
  saleNumber,
  productSaleId,
  saleData,
  customer,
  paymentMethod
}: ProductSaleSuccessModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSendDropdown, setShowSendDropdown] = useState(false);
  const [isInvoiceEmailModalOpen, setIsInvoiceEmailModalOpen] = useState(false);
  const [isInvoiceSMSModalOpen, setIsInvoiceSMSModalOpen] = useState(false);
  const { selectedBusiness } = useBusiness();
  const invoiceRef = React.useRef<HTMLDivElement>(null);

  const isPaid = paymentMethod !== 'invoice' && paymentMethod !== 'pending';
  const amountOwing = isPaid ? 0 : saleData.total;
  const hasStripeConnected = !!(selectedBusiness?.stripe_connect_account_id && (selectedBusiness as any)?.online_payments_enabled !== false);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = () => setShowSendDropdown(false);
    if (showSendDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSendDropdown]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!productSaleId) {
      alert('Sale ID not available for PDF generation.');
      return;
    }

    setDownloadingPdf(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: { productSaleId }
      });
      if (error || !data?.base64) {
        throw new Error('PDF generation failed');
      }
      const bin = atob(data.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });

      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const file = new File([blob], data.filename, { type: 'application/pdf' });
          if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: data.filename }).catch(() => {});
            return;
          }
        }
      } catch {}

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || `receipt-${saleNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const generateInvoicePDF = async (): Promise<{ blob: Blob; filename: string; base64: string } | null> => {
    try {
      if (!productSaleId) return null;
      const { data, error } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: { productSaleId }
      });
      if (error || !data?.base64) return null;
      const bin = atob(data.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      return { blob, filename: data.filename || `receipt-${saleNumber}.pdf`, base64: data.base64 };
    } catch (error) {
      console.error('Error generating PDF:', error);
      return null;
    }
  };

  const generatePaymentLink = async (saveCardOnFile: boolean): Promise<string | null> => {
    if (!selectedBusiness || !customer) return null;
    try {
      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .insert({
          business_id: selectedBusiness.id,
          booking_id: null,
          product_sale_id: productSaleId,
          amount: saleData.total,
          method: 'credit_card',
          status: 'pending',
          reference: `Product Sale ${saleNumber}`,
          notes: `Product sale payment for ${saleNumber}`,
          payment_token: crypto.randomUUID(),
          save_card_on_file: !!(saveCardOnFile && customer.id),
          save_card_customer_id: saveCardOnFile && customer.id ? customer.id : null,
          metadata: {
            type: 'product_sale',
            sale_number: saleNumber,
            customer_id: customer.id
          }
        })
        .select()
        .single();

      if (paymentError || !payment) return null;

      const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
        body: { paymentId: payment.id }
      });

      if (linkError || !linkData?.paymentUrl) return null;
      return linkData.paymentUrl;
    } catch {
      return null;
    }
  };

  const handleSendInvoiceEmail = async (
    emailSubject: string,
    emailMessage: string,
    allowStripePay: boolean,
    _depositAmount?: number,
    saveCardOnFile?: boolean,
  ) => {
    if (!customer?.email || !selectedBusiness) {
      alert('Customer email not found');
      return;
    }

    setIsInvoiceEmailModalOpen(false);

    try {
      setIsSending(true);

      const pdfData = await generateInvoicePDF();

      let paymentLinkHtml = '';
      if (allowStripePay && amountOwing > 0 && hasStripeConnected) {
        const paymentUrl = await generatePaymentLink(!!saveCardOnFile);
        if (paymentUrl) {
          paymentLinkHtml = `
            <div style="margin: 20px 0; padding: 15px; background-color: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 8px;">
              <h3 style="color: #0369a1; margin: 0 0 10px 0;">Pay Online</h3>
              <p style="margin: 0 0 15px 0; color: #374151;">Click the button below to pay securely:</p>
              <a href="${paymentUrl}" style="display: inline-block; background-color: #0ea5e9; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                Pay $${saleData.total.toFixed(2)} Now
              </a>
            </div>
          `;
        }
      }

      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="padding: 32px;">
            ${emailMessage.replace(/\n/g, '<br>')}
            ${paymentLinkHtml}
          </div>
          <div style="padding: 24px 32px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
            <p style="color: #9ca3af; font-size: 13px; margin: 0; text-align: center;">This is an automated invoice from ${selectedBusiness.name}</p>
          </div>
        </div>
      `;

      const attachments: Array<{ filename: string; content: string; type: string }> = [];
      if (pdfData) {
        attachments.push({
          filename: pdfData.filename,
          content: pdfData.base64,
          type: 'application/pdf'
        });
      }

      const emailResult = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: customer.email,
        subject: emailSubject,
        body: emailBody,
        attachments: attachments.length > 0 ? attachments : undefined
      });

      if (emailResult.success) {
        alert('Invoice sent successfully via email!');
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send invoice email:', error);
      alert('Failed to send invoice. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendInvoiceSMS = async (
    customMessage: string,
    allowStripePay: boolean,
    _depositAmount?: number,
    saveCardOnFile?: boolean,
  ) => {
    if (!customer?.phone || !selectedBusiness) {
      alert('Customer phone number not found');
      return;
    }

    setIsInvoiceSMSModalOpen(false);

    try {
      setIsSending(true);

      let finalMessage = customMessage;

      if (allowStripePay && amountOwing > 0 && hasStripeConnected) {
        const paymentUrl = await generatePaymentLink(!!saveCardOnFile);
        if (paymentUrl) {
          finalMessage += `\n\nPay online: ${paymentUrl}`;
        }
      }

      const smsResult = await sendSMS({
        businessId: selectedBusiness.id,
        to: customer.phone,
        message: finalMessage,
        customerId: customer.id,
      });

      if (smsResult.success) {
        alert('Invoice sent successfully via SMS!');
      } else {
        throw new Error(smsResult.error || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Failed to send invoice SMS:', error);
      alert('Failed to send invoice. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const saleForInvoice = {
    saleNumber,
    saleData: {
      ...saleData,
      discounts: saleData.discountDetails ? [{
        id: 'discount-1',
        title: saleData.discountDetails.source === 'code' 
          ? `Code: ${saleData.discountDetails.code}` 
          : (saleData.discountDetails.description || 'Custom Discount'),
        type: saleData.discountDetails.type,
        amount: saleData.discountDetails.value
      }] : undefined,
      total_discounts: saleData.discountAmount,
      subtotal_after_discounts: saleData.discountAmount ? saleData.subtotal - saleData.discountAmount : undefined
    },
    paymentMethod,
    saleDate: new Date().toISOString()
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100040]`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="flex items-center justify-center min-h-screen p-4">
        <div 
          className={`relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl transform transition-all duration-300 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Sale Complete!</h2>
                  <p className="text-green-100 text-sm">Payment processed successfully</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Invoice Display */}
          <div className="max-h-[70vh] overflow-y-auto">
            {selectedBusiness && (
              <StyledInvoiceDisplay
                sale={saleForInvoice}
                business={selectedBusiness}
                customer={customer}
                forwardRef={invoiceRef}
                className="invoice-preview"
              />
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              {downloadingPdf ? 'Generating...' : 'Download PDF'}
            </button>

            {/* Send dropdown */}
            <div className="flex-1 relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!customer) {
                    alert('No customer information available.');
                    return;
                  }
                  setShowSendDropdown(!showSendDropdown);
                }}
                disabled={isSending || !customer}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {isSending ? 'Sending...' : 'Send Receipt'}
              </button>

              {showSendDropdown && (
                <div
                  className="absolute left-0 bottom-full mb-2 w-48 rounded-xl shadow-lg bg-white border border-gray-200 z-20 overflow-hidden"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => {
                      setShowSendDropdown(false);
                      setIsInvoiceEmailModalOpen(true);
                    }}
                    disabled={!customer?.email}
                    className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Mail className="h-4 w-4 mr-3 text-blue-500" />
                    Email
                  </button>
                  <button
                    onClick={() => {
                      setShowSendDropdown(false);
                      setIsInvoiceSMSModalOpen(true);
                    }}
                    disabled={!customer?.phone}
                    className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed border-t border-gray-100"
                    style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <MessageSquare className="h-4 w-4 mr-3 text-green-500" />
                    SMS
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      <InvoiceEmailModal
        isOpen={isInvoiceEmailModalOpen}
        onClose={() => setIsInvoiceEmailModalOpen(false)}
        onSend={handleSendInvoiceEmail}
        customerEmail={customer?.email || ''}
        customerName={customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : 'Customer'}
        businessName={selectedBusiness?.name || 'Business'}
        bookingNumber={parseInt(saleNumber) || 0}
        hasStripeConnected={hasStripeConnected}
        amountOwing={amountOwing}
        canOfferSaveCardOnFile={!!customer?.id}
        isSending={isSending}
        initialSubject={`Invoice #${saleNumber} from ${selectedBusiness?.name || 'Business'}`}
        initialMessage={`Hi ${customer?.first_name || 'there'},\n\nPlease find attached your invoice #${saleNumber} from ${selectedBusiness?.name || 'our business'}.\n\nTotal: $${saleData.total.toFixed(2)}\n\nThank you for your business!`}
      />
      <InvoiceSMSModal
        isOpen={isInvoiceSMSModalOpen}
        onClose={() => setIsInvoiceSMSModalOpen(false)}
        onSend={handleSendInvoiceSMS}
        customerPhone={customer?.phone || ''}
        customerName={customer?.first_name || 'there'}
        businessName={selectedBusiness?.name || 'Business'}
        bookingNumber={parseInt(saleNumber) || 0}
        hasStripeConnected={hasStripeConnected}
        amountOwing={amountOwing}
        canOfferSaveCardOnFile={!!customer?.id}
        isSending={isSending}
        initialMessage={`Hi ${customer?.first_name || 'there'}, here is your invoice #${saleNumber} from ${selectedBusiness?.name || 'our business'}. Total: $${saleData.total.toFixed(2)}.`}
      />
    </>
  );
}
